// UploadFile.jsx
import React, { useState, useCallback } from "react";
import { Upload, Button, message, Progress } from "antd";
import {
  UploadOutlined,
  LoadingOutlined,
  FileOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  FileTextOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import CustomModal from "../../../components/CustomModal";
import { useSelector } from "react-redux";
import {
  generateImageThumbnail,
  getThumbnailBlobOrURL,
} from "../../../utils/common";
import folderIcon from "../../../assets/svg/folderIcon.svg";
import { encryptFileWithIV } from "../../../utils/FileEncryptor";
import { handlePostRequest } from "../../../redux/action";
import { getCategoryType } from "../../../Common";
import {
  AUDIO_TYPES,
  DOC_TYPES,
  IMAGE_TYPES,
  VIDEO_TYPES,
} from "../../../constants/filesExtensions";
import { debugLog } from "../../../utils/debugLog";
import UploadDropZone from "./UploadDropZone";
import FileListViewer from "./FileListViewer";
import UploadProgressViewer from "./UploadProgressViewer";
import { useTranslation } from "react-i18next";
import { getFileCount, getTotalVolume } from "../../../redux/action/file";

const UploadFile = ({ visible, setVisible }) => {
  const { encryptionKeys } = useSelector((state) => state.authSlice);
  const { Date, ivKey, spaceKey } = encryptionKeys ?? {};
  const key = spaceKey;
  const iv = ivKey;
  const date = Date;
  const { t } = useTranslation();
  // 1GB limit in bytes (1024 * 1024 * 1024)
  const MAX_TOTAL_SIZE = 1073741824;

  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);

  // Calculate total size of selected files
  const getTotalSize = (files) => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  // Check if adding new files will exceed limit
  const checkSizeLimit = (newFiles, existingFiles = fileList) => {
    const currentSize = getTotalSize(existingFiles);
    const newFilesSize = getTotalSize(newFiles);
    const totalSize = currentSize + newFilesSize;

    return {
      isValid: totalSize <= MAX_TOTAL_SIZE,
      totalSize,
      currentSize,
      newFilesSize,
      exceededBy: totalSize - MAX_TOTAL_SIZE,
    };
  };

  const handleSingleFileUpload = async (selectedFile, index, total) => {
    try {
      const fileExtension = selectedFile.name.split(".").pop();
      const mimeType = selectedFile.type || "application/octet-stream";
      debugLog(
        "🚀 ~ handleSingleFileUpload ~ mimeType:",
        "log",
        getCategoryType(mimeType)
      );
      const fileName = selectedFile.name;
      const keyCreationDate = date;

      // Calculate progress based on current file and overall progress
      const baseProgress = (index / total) * 100;
      const fileProgressStep = 100 / total;

      setUploadProgress(baseProgress + fileProgressStep * 0.1);

      // 1. Generate Thumbnail
      const thumbnailBlob = await getThumbnailBlobOrURL(selectedFile);
      debugLog(
        "🚀 ~ handleSingleFileUpload ~ thumbnailBlob:",
        "log",
        thumbnailBlob
      );

      setUploadProgress(baseProgress + fileProgressStep * 0.3);

      const thumbnailBlobencryptedFile = await encryptFileWithIV(
        thumbnailBlob,
        iv,
        key
      );

      const encryptedFile = await encryptFileWithIV(selectedFile, iv, key);

      setUploadProgress(baseProgress + fileProgressStep * 0.7);

      const formData = new FormData();
      formData.append("files", encryptedFile, selectedFile.name);
      formData.append(
        "files",
        thumbnailBlobencryptedFile,
        `thumbnail_${selectedFile.name}`
      );
      formData.append("type", getCategoryType(mimeType));
      formData.append("ext", fileExtension);
      formData.append("mimeType", mimeType);
      formData.append("fileName", fileName);
      formData.append("keyCreationDate", keyCreationDate);
      formData.append("directory", "/");
      debugLog("thumbnail_Name", "log", `thumbnail_${selectedFile.name}`);

      setUploadProgress(baseProgress + fileProgressStep * 0.9);

      await handlePostRequest("upload/v1", formData);

      setUploadProgress(baseProgress + fileProgressStep);

      // Add to uploaded files
      setUploadedFiles((prev) => [...prev, selectedFile.name]);

      return { success: true, fileName: selectedFile.name };
    } catch (err) {
      debugLog("❌ Upload failed for file:", "error", selectedFile.name, err);

      // Add to failed files
      setFailedFiles((prev) => [...prev, selectedFile.name]);

      return { success: false, fileName: selectedFile.name, error: err };
    }
  };

  const handleMultipleUpload = async () => {
    if (fileList.length === 0) return;

    // Final size check before upload
    const sizeCheck = checkSizeLimit([]);
    if (!sizeCheck.isValid) {
      message.error(
        "Total file size exceeds 1GB limit. Please remove some files."
      );
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setCurrentUploadIndex(0);
      setUploadedFiles([]);
      setFailedFiles([]);

      const totalFiles = fileList.length;
      const results = [];

      for (let i = 0; i < totalFiles; i++) {
        setCurrentUploadIndex(i);
        const result = await handleSingleFileUpload(fileList[i], i, totalFiles);
        results.push(result);
      }

      // Show final results
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount === totalFiles) {
        message.success(`All ${totalFiles} files uploaded successfully!`);
      } else if (successCount > 0) {
        message.warning(`${successCount} files uploaded, ${failCount} failed`);
      } else {
        message.error("All files failed to upload");
      }

      setTimeout(() => {
        getFileCount();
        setVisible(false);
        getTotalVolume();
        getFileCount();
        setFileList([]);
        setUploadProgress(0);
        setCurrentUploadIndex(0);
        setUploadedFiles([]);
        setFailedFiles([]);
      }, 2000);
    } catch (err) {
      debugLog("❌ Multi-upload failed:", "error", err);

      message.error("Upload process failed");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setFileList([]);
    setUploadProgress(0);
    setCurrentUploadIndex(0);
    setUploadedFiles([]);
    setFailedFiles([]);
  };

  const handleOk = () => {
    if (fileList.length === 0) {
      message.error("Please select files to upload.");
      return;
    }

    // Check size limit before upload
    const sizeCheck = checkSizeLimit([]);
    if (!sizeCheck.isValid) {
      message.error(
        `Total file size (${formatFileSize(
          sizeCheck.totalSize
        )}) exceeds 1GB limit. Please remove ${formatFileSize(
          sizeCheck.exceededBy
        )} worth of files.`
      );
      return;
    }

    debugLog("Uploading files:", "log", fileList);
    handleMultipleUpload();
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);

        // Check size limit
        const sizeCheck = checkSizeLimit(files);
        if (!sizeCheck.isValid) {
          message.error(
            `Adding these files would exceed the 1GB limit by ${formatFileSize(
              sizeCheck.exceededBy
            )}. Please select smaller files.`
          );
          return;
        }

        setFileList((prev) => [...prev, ...files]);
      }
    },
    [fileList]
  );

  const removeFile = (indexToRemove) => {
    setFileList((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removeAllFiles = () => {
    setFileList([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileType = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();

    const typeMapping = {
      Image: IMAGE_TYPES,
      Video: VIDEO_TYPES,
      Audio: AUDIO_TYPES,
      Document: DOC_TYPES,
    };

    for (const [type, extensions] of Object.entries(typeMapping)) {
      if (extensions.includes(extension)) {
        return type;
      }
    }

    return "File";
  };

  const getFileStatus = (fileName) => {
    if (uploadedFiles.includes(fileName)) return "success";
    if (failedFiles.includes(fileName)) return "failed";
    if (
      uploading &&
      currentUploadIndex < fileList.length &&
      fileList[currentUploadIndex].name === fileName
    )
      return "uploading";
    return "pending";
  };

  // Calculate current usage percentage
  const currentTotalSize = getTotalSize(fileList);
  const usagePercentage = (currentTotalSize / MAX_TOTAL_SIZE) * 100;
  const isNearLimit = usagePercentage > 80;
  const isOverLimit = usagePercentage > 100;

  const props = {
    beforeUpload: (file) => {
      // Check if adding this file will exceed limit
      const sizeCheck = checkSizeLimit([file]);
      if (!sizeCheck.isValid) {
        message.error(
          `Adding "${file.name}" (${formatFileSize(
            file.size
          )}) would exceed the 1GB limit by ${formatFileSize(
            sizeCheck.exceededBy
          )}.`
        );
        return false;
      }

      setFileList((prev) => [...prev, file]);
      return false;
    },
    onRemove: removeFile,
    fileList: [],
    disabled: uploading,
    showUploadList: false,
    multiple: true,
  };
  return (
    <CustomModal
      title={<div className="upload-modal-title"></div>}
      visible={visible}
      handleCancel={handleCancel}
      cancelBtnText={t("common.cancel")}
      footer={false}
      width={700}
      className="my-custom-modal"
    >
      <div className="upload-container">
        <div className="circular-icon-wrapper">
          <img src={folderIcon} />
        </div>

        <div className="upload-section">
          <span className="upload-heading">{t("uploadModal.heading")}</span>
          <span className="upload-subtext">{t("uploadModal.subtext")}</span>
        </div>

        {fileList.length > 0 && (
          <div
            className={`size-indicator ${isNearLimit ? "warning" : ""} ${
              isOverLimit ? "error" : ""
            }`}
          >
            <div className="size-info">
              <div className="size-text">
                <span className="current-size">
                  {formatFileSize(currentTotalSize)}
                </span>
                <span className="separator"> / </span>
                <span className="max-size">1 GB</span>
                <span className="percentage">
                  ({Math.round(usagePercentage)}%)
                </span>
              </div>
              {isOverLimit && (
                <div className="size-warning">
                  <WarningOutlined />
                  <span>{t("uploadModal.sizeLimitExceeded")}</span>
                </div>
              )}
            </div>
            <div className="size-progress">
              <div
                className={`size-progress-bar ${
                  isOverLimit
                    ? "over-limit"
                    : isNearLimit
                    ? "near-limit"
                    : "normal"
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
          </div>
        )}

        <div style={{ textAlign: "right" }}>
          <UploadDropZone
            data={{
              dragActive,
              fileList,
              uploading,
              isOverLimit,
              handleDragIn,
              handleDragOut,
              handleDrag,
              handleDrop,
              uploadProps: props,
            }}
          />
          <span className="max-size-label">
            {t("uploadModal.maxSizeLabel")}
          </span>
        </div>

        <FileListViewer
          data={{
            fileList,
            currentTotalSize,
            uploading,
            removeAllFiles,
            removeFile,
            getFileStatus,
            formatFileSize,
            getFileType,
          }}
        />
      </div>

      <div className="custom-modal-footer">
        <Button
          type="primary"
          onClick={handleOk}
          className="continue-btn primary-btn"
          disabled={uploading || fileList.length === 0 || isOverLimit}
        >
          {uploading
            ? `Uploading... (${currentUploadIndex + 1}/${fileList.length})`
            : `Continue ${fileList.length} File${
                fileList.length > 1 ? "s" : ""
              }`}
        </Button>
      </div>
    </CustomModal>
  );
};

export default UploadFile;
